import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Paperclip,
  Mic,
  MicOff,
  ArrowUp,
  ChevronRight,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useWorkspaceStore } from '../store/workspace';
import { AI_EMPLOYEES } from '../data/employees';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface DashboardStats {
  messages_today: number;
  tasks_completed: number;
  brain_documents: number;
  time_saved_hours: number;
}

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
};

// Use cases data - quick action cards like Sintra
const USE_CASES = [
  {
    title: 'Research my competitors',
    category: 'Business',
    employee: 'sage',
    employeeAvatar: '🧠',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    title: 'Create a post for my social media',
    category: 'Content',
    employee: 'aria',
    employeeAvatar: '🎀',
    gradient: 'from-pink-500 to-rose-500',
  },
  {
    title: 'Build a high-converting website',
    category: 'Marketing',
    employee: 'ink',
    employeeAvatar: '✍️',
    gradient: 'from-orange-500 to-amber-500',
  },
  {
    title: "Summarize today's emails",
    category: 'Productivity',
    employee: 'sync',
    employeeAvatar: '📅',
    gradient: 'from-green-500 to-emerald-500',
  },
];

// Get employees that are not coming soon (active helpers)
const ACTIVE_EMPLOYEES = AI_EMPLOYEES.filter(e => !e.isComingSoon);

// Keyword-to-employee routing map for intelligent query analysis
const EMPLOYEE_KEYWORDS: Record<string, string[]> = {
  // Orbit - SEO Expert
  orbit: [
    'seo', 'search engine', 'keyword', 'keywords', 'ranking', 'rankings', 'backlink', 'backlinks',
    'organic traffic', 'google ranking', 'search optimization', 'meta tags', 'meta description',
    'serp', 'search results', 'domain authority', 'page rank', 'on-page', 'off-page', 'technical seo',
    'local seo', 'google search', 'bing', 'crawl', 'index', 'sitemap', 'robots.txt'
  ],
  // Atlas - Data Analyst & Algorithm Expert
  atlas: [
    'data analysis', 'data analyst', 'analyze data', 'algorithm', 'algorithms', 'statistics',
    'statistical', 'csv', 'json', 'excel', 'spreadsheet', 'chart', 'graph', 'visualization',
    'metrics', 'kpi', 'dashboard data', 'data processing', 'machine learning', 'ai model',
    'prediction', 'forecast', 'regression', 'correlation', 'data science', 'big data',
    'database', 'sql', 'query', 'analytics', 'report', 'insights from data'
  ],
  // Aria - Social Media Manager
  aria: [
    'social media', 'instagram', 'facebook', 'twitter', 'tiktok', 'linkedin post', 'post',
    'content calendar', 'hashtag', 'hashtags', 'viral', 'engagement', 'followers', 'following',
    'social post', 'caption', 'reels', 'stories', 'social strategy', 'influencer', 'trending',
    'social presence', 'community', 'social content'
  ],
  // Ink - Copywriter
  ink: [
    'copywriting', 'copy', 'ad copy', 'headline', 'tagline', 'landing page', 'sales page',
    'product description', 'blog post', 'article', 'content writing', 'write content',
    'persuasive', 'compelling copy', 'brand voice', 'messaging', 'slogan', 'call to action',
    'cta', 'website copy', 'marketing copy'
  ],
  // Echo - Email Marketing
  echo: [
    'email', 'newsletter', 'email campaign', 'email marketing', 'subject line', 'open rate',
    'click rate', 'email sequence', 'drip campaign', 'autoresponder', 'mailchimp', 'email list',
    'subscribers', 'unsubscribe', 'email automation', 'cold email', 'email template'
  ],
  // Sage - Business Strategist
  sage: [
    'business strategy', 'competitor', 'competitors', 'competition', 'market analysis',
    'swot', 'growth strategy', 'business plan', 'market research', 'industry analysis',
    'competitive advantage', 'business model', 'revenue model', 'pricing strategy',
    'go to market', 'gtm', 'market opportunity', 'target market', 'business advice'
  ],
  // Spark - Sales Specialist
  spark: [
    'sales', 'selling', 'close deal', 'prospect', 'prospecting', 'lead', 'leads', 'pipeline',
    'sales pitch', 'cold call', 'outreach', 'proposal', 'quotation', 'quote', 'negotiate',
    'objection', 'crm', 'salesforce', 'hubspot sales', 'b2b sales', 'b2c sales', 'conversion'
  ],
  // Nova - Customer Support
  nova: [
    'customer support', 'customer service', 'help desk', 'ticket', 'complaint', 'refund',
    'support response', 'faq', 'knowledge base', 'customer inquiry', 'escalation',
    'customer satisfaction', 'csat', 'nps', 'support team', 'live chat', 'chatbot'
  ],
  // Flux - eCommerce Manager
  flux: [
    'ecommerce', 'e-commerce', 'online store', 'shopify', 'woocommerce', 'product listing',
    'inventory', 'shopping cart', 'checkout', 'order', 'orders', 'shipping', 'fulfillment',
    'amazon', 'ebay', 'marketplace', 'dropshipping', 'upsell', 'cross-sell'
  ],
  // Code - Developer Assistant
  code: [
    'code', 'coding', 'programming', 'developer', 'development', 'bug', 'debug', 'api',
    'javascript', 'python', 'react', 'node', 'database', 'backend', 'frontend', 'fullstack',
    'git', 'github', 'deploy', 'deployment', 'server', 'docker', 'aws', 'cloud'
  ],
  // Pixel - Design Assistant
  pixel: [
    'design', 'ui', 'ux', 'user interface', 'user experience', 'wireframe', 'mockup',
    'prototype', 'figma', 'sketch', 'adobe', 'photoshop', 'illustrator', 'logo',
    'branding', 'color palette', 'typography', 'visual design', 'graphic design'
  ],
  // Sync - Virtual Assistant
  sync: [
    'schedule', 'scheduling', 'calendar', 'meeting', 'appointment', 'reminder', 'task',
    'to-do', 'todo', 'organize', 'planning', 'travel', 'booking', 'reservation',
    'expense', 'time management', 'productivity', 'assistant'
  ],
  // Pulse - Personal Growth
  pulse: [
    'fitness', 'workout', 'exercise', 'health', 'wellness', 'diet', 'nutrition',
    'habit', 'goal setting', 'motivation', 'mindfulness', 'meditation', 'self-improvement',
    'personal development', 'coaching', 'life coach', 'mental health'
  ],
  // Scout - Recruiter
  scout: [
    'hiring', 'recruit', 'recruitment', 'job description', 'candidate', 'interview',
    'resume', 'cv', 'onboarding', 'talent', 'hr', 'human resources', 'job posting',
    'applicant', 'headhunting', 'employer branding'
  ],
  // Legal - Compliance Assistant
  legal: [
    'legal', 'contract', 'agreement', 'terms of service', 'privacy policy', 'compliance',
    'gdpr', 'regulation', 'policy', 'disclaimer', 'liability', 'intellectual property',
    'trademark', 'copyright', 'nda', 'non-disclosure'
  ],
  // Slides - Presentation Maker
  slides: [
    'presentation', 'powerpoint', 'slides', 'pitch deck', 'keynote', 'google slides',
    'slideshow', 'presenter', 'deck', 'slide design', 'infographic'
  ],
};

// Function to analyze user input and determine the best employee
function analyzeQueryAndRoute(query: string): string {
  const lowerQuery = query.toLowerCase();

  // Score each employee based on keyword matches
  const scores: Record<string, number> = {};

  for (const [employeeId, keywords] of Object.entries(EMPLOYEE_KEYWORDS)) {
    scores[employeeId] = 0;
    for (const keyword of keywords) {
      if (lowerQuery.includes(keyword)) {
        // Longer keyword matches are weighted higher
        scores[employeeId] += keyword.split(' ').length;
      }
    }
  }

  // Find the employee with the highest score
  let bestEmployee = 'agent'; // Default to agent
  let highestScore = 0;

  for (const [employeeId, score] of Object.entries(scores)) {
    if (score > highestScore) {
      highestScore = score;
      bestEmployee = employeeId;
    }
  }

  // Only route to specific employee if we have a meaningful match
  return highestScore > 0 ? bestEmployee : 'agent';
}

// Extend Window interface for SpeechRecognition
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

export default function Dashboard() {
  const { user, token } = useAuthStore();
  const { theme } = useThemeStore();
  const { currentWorkspace } = useWorkspaceStore();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Track window resize for responsive placeholder
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Credits display - get from user object
  const credits = user?.credits_remaining || 0;
  const maxCredits = user?.subscription_tier === 'pro' ? 1500 : user?.subscription_tier === 'professional' ? 3000 : user?.subscription_tier === 'enterprise' ? 10000 : user?.subscription_tier === 'student' ? 1500 : 100;
  // Use actual expiration date from database - only show if it exists
  const creditsResetDate = user?.credits_expires_at ? new Date(user.credits_expires_at) : null;
  // Format the expiration date string
  const creditsResetDateStr = creditsResetDate
    ? creditsResetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!token) return;

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const statsRes = await fetch(`${API_URL}/dashboard/stats`, { headers });

        if (statsRes.ok) {
          const statsData = await statsRes.json() as { success: boolean; data: DashboardStats };
          if (statsData.success) setStats(statsData.data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [chatInput]);

  // Handle chat submission - intelligently route to the best employee based on query
  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || isSending) return;

    setIsSending(true);

    // Analyze the user's query and determine the best employee to handle it
    const targetEmployee = analyzeQueryAndRoute(chatInput.trim());

    // Navigate to the appropriate employee with the initial message
    navigate(`/dashboard/employees/${targetEmployee}?message=${encodeURIComponent(chatInput.trim())}`);
  };

  // Handle use case click
  const handleUseCaseClick = (useCase: typeof USE_CASES[0]) => {
    navigate(`/dashboard/employees/${useCase.employee}?message=${encodeURIComponent(useCase.title)}`);
  };

  // Handle file upload - route to Pixel (design employee) with the image
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Store the file in sessionStorage temporarily for Pixel to access
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      sessionStorage.setItem('pendingImage', base64);
      sessionStorage.setItem('pendingImageName', file.name);

      // Navigate to Pixel with the image - don't auto-send a message
      // so Pixel can ask the user what they want to do with it
      navigate(`/dashboard/employees/pixel?hasImage=true`);
    };
    reader.readAsDataURL(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [navigate]);

  // Track the confirmed text (final results) separately from interim display
  const confirmedVoiceTextRef = useRef('');
  const voiceBaseTextRef = useRef(''); // Text that was in the input before voice started

  // Handle voice input toggle
  const handleVoiceInput = useCallback(() => {
    // Check if browser supports speech recognition
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) {
      alert('Voice input is not supported in your browser. Please try Chrome or Edge.');
      return;
    }

    if (isListening) {
      // Stop listening
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      return;
    }

    // Save the current input text as base, and reset confirmed voice text
    voiceBaseTextRef.current = chatInput;
    confirmedVoiceTextRef.current = '';

    // Start listening
    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // When we get a final result, add it to confirmed text
      if (finalTranscript) {
        confirmedVoiceTextRef.current += finalTranscript;
      }

      // Show real-time text: base text + confirmed voice text + interim text
      const baseText = voiceBaseTextRef.current;
      const separator = baseText && !baseText.endsWith(' ') ? ' ' : '';
      const displayText = baseText + separator + confirmedVoiceTextRef.current + interimTranscript;
      setChatInput(displayText);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, chatInput]);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Get workspace name - use selected workspace name
  const workspaceName = currentWorkspace?.name === 'My Workspace'
    ? 'My Workspace'
    : currentWorkspace?.name
      ? `${currentWorkspace.name}'s Workspace`
      : (user?.name ? `${user.name.split(' ')[0]}'s Workspace` : 'My Workspace');

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="max-w-6xl mx-auto"
    >
      {/* Workspace Header with Gradient Background - Sintra Style */}
      <motion.div
        variants={fadeInUp}
        className="relative overflow-hidden rounded-2xl sm:rounded-3xl mb-6 sm:mb-8"
      >
        {/* Gradient Background */}
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-purple-900/30 via-purple-800/20 to-transparent' : 'bg-gradient-to-b from-purple-100 via-purple-50 to-transparent'}`} />

        <div className="relative pt-8 sm:pt-12 pb-6 sm:pb-8 px-3 sm:px-4 text-center">
          {/* Workspace Title */}
          <motion.h1
            className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8 ${isDark ? 'text-purple-300' : 'text-purple-700'}`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {workspaceName}
          </motion.h1>

          {/* Main Chat Input - Sintra Style */}
          <motion.form
            onSubmit={handleChatSubmit}
            className="max-w-2xl mx-auto px-1 sm:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className={`relative rounded-xl sm:rounded-2xl border shadow-lg ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}>
              <textarea
                ref={textareaRef}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleChatSubmit(e);
                  }
                }}
                placeholder={isMobile ? "Ask SweetPilot anything..." : "How can SweetPilot help you today?"}
                className={`w-full px-3 sm:px-5 py-3 sm:py-4 pr-24 sm:pr-32 rounded-xl sm:rounded-2xl resize-none focus:outline-none text-sm sm:text-base ${isDark ? 'bg-transparent text-white placeholder-gray-500' : 'bg-transparent text-slate-900 placeholder-slate-400'}`}
                rows={1}
                style={{ minHeight: '48px' }}
              />

              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                className="hidden"
              />

              {/* Input Actions */}
              <div className="absolute right-2 sm:right-3 bottom-2 sm:bottom-3 flex items-center gap-1 sm:gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}`}
                  title="Attach image (opens Pixel)"
                >
                  <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <div className="relative flex items-center justify-center" title="Speak Now">
                  {isListening && (
                    <div className="absolute w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-red-500/20 animate-ping" />
                  )}
                  <button
                    type="button"
                    onClick={handleVoiceInput}
                    className={`relative p-1.5 sm:p-2 rounded-full transition-colors ${
                      isListening
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                        : isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {isListening ? <MicOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Mic className="w-4 h-4 sm:w-5 sm:h-5" />}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={!chatInput.trim() || isSending}
                  className={`p-1.5 sm:p-2 rounded-lg transition-all ${
                    chatInput.trim()
                      ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white hover:opacity-90'
                      : isDark ? 'bg-white/10 text-gray-500' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {isSending ? (
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  ) : (
                    <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </button>
              </div>
            </div>
          </motion.form>

          {/* Credits Display */}
          <motion.p
            className={`text-sm mt-4 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {credits || 0} of {maxCredits} credits left.{creditsResetDateStr ? ` Expires on ${creditsResetDateStr}` : ''}
            <Link to="/dashboard/settings?tab=billing" className="ml-2 px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-xs font-medium">
              Upgrade
            </Link>
          </motion.p>
        </div>
      </motion.div>

      {/* Use Cases Section - Sintra Style */}
      <motion.div variants={fadeInUp} className="mb-8 sm:mb-10">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Use cases
          </h2>
          <Link
            to="/dashboard/employees"
            className={`text-sm flex items-center gap-1 ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
          >
            See all
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {USE_CASES.map((useCase, index) => (
            <motion.button
              key={useCase.title}
              variants={scaleIn}
              whileHover={{ y: -4 }}
              onClick={() => handleUseCaseClick(useCase)}
              className={`p-3 sm:p-4 rounded-xl border text-left transition-all group ${isDark ? 'bg-dark-800/50 border-white/5 hover:border-white/20' : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'}`}
            >
              <h3 className={`font-medium mb-2 sm:mb-3 line-clamp-2 text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {useCase.title}
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br ${useCase.gradient} flex items-center justify-center`}>
                    <span className="text-xs sm:text-sm">{useCase.employeeAvatar}</span>
                  </div>
                  <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                    {useCase.category}
                  </span>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Helpers Section - Sintra Style Employee Grid */}
      <motion.div variants={fadeInUp}>
        <h2 className={`text-base sm:text-lg font-semibold mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Helpers
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {ACTIVE_EMPLOYEES.slice(0, 12).map((employee) => (
            <motion.div
              key={employee.id}
              variants={scaleIn}
              whileHover={{ y: -8 }}
            >
              <Link
                to={`/dashboard/employees/${employee.id}`}
                className="block rounded-xl sm:rounded-2xl overflow-hidden group relative"
              >
                {/* Employee Card with Gradient Background */}
                <div className={`aspect-[4/5] bg-gradient-to-br ${employee.gradient} p-3 sm:p-4 flex flex-col justify-end relative overflow-hidden`}>
                  {/* Large Emoji Avatar - Centered */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-90">
                    <span className="text-6xl sm:text-8xl transform group-hover:scale-110 transition-transform duration-300">
                      {employee.avatar}
                    </span>
                  </div>

                  {/* Overlay gradient for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                  {/* Platform badges - like Sintra's LinkedIn icons */}
                  <div className="absolute top-2 sm:top-3 right-2 sm:right-3 flex gap-1">
                    {employee.category === 'marketing' && (
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-white text-[10px] sm:text-xs font-bold">in</span>
                      </div>
                    )}
                  </div>

                  {/* NEW Badge */}
                  {employee.isNew && (
                    <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
                      <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs bg-green-500 text-white rounded-full font-medium">
                        NEW
                      </span>
                    </div>
                  )}

                  {/* Employee Info */}
                  <div className="relative z-10">
                    <h3 className="text-white font-bold text-sm sm:text-lg">
                      {employee.name}
                    </h3>
                    <p className="text-white/80 text-xs sm:text-sm line-clamp-2">
                      {employee.role}
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* See All Employees Link */}
        {ACTIVE_EMPLOYEES.length > 12 && (
          <div className="text-center mt-4 sm:mt-6">
            <Link
              to="/dashboard/employees"
              className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity text-sm sm:text-base"
            >
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
              See all {ACTIVE_EMPLOYEES.length} employees
            </Link>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
